const DATABASE_UNAVAILABLE_CODES = new Set([
  "ECONNREFUSED",
  "ECONNRESET",
  "ENOTFOUND",
  "ETIMEDOUT",
  "EAI_AGAIN",
  "UND_ERR_CONNECT_TIMEOUT",
  "UND_ERR_HEADERS_TIMEOUT",
  "UND_ERR_SOCKET",
])

const DATABASE_UNAVAILABLE_STATUSES = new Set([500, 502, 503, 504])

const DATABASE_UNAVAILABLE_MESSAGES = [
  /bad gateway/i,
  /connect timeout/i,
  /database unavailable/i,
  /econnrefused/i,
  /econnreset/i,
  /enotfound/i,
  /etimedout/i,
  /fetch failed/i,
  /gateway timeout/i,
  /headers timeout/i,
  /network error/i,
  /service unavailable/i,
  /socket hang up/i,
  /und_err_/i,
]

type ErrorLike = {
  cause?: unknown
  code?: unknown
  errors?: unknown
  message?: unknown
  name?: unknown
  response?: unknown
  status?: unknown
  statusCode?: unknown
}

export class DatabaseUnavailableError extends Error {
  cause?: unknown

  constructor(message = "Database unavailable", cause?: unknown) {
    super(message)
    this.name = "DatabaseUnavailableError"
    this.cause = cause
  }
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === "object"

const toStatus = (value: unknown) => {
  if (typeof value === "number") return value
  if (typeof value === "string") {
    const parsed = Number.parseInt(value, 10)
    return Number.isNaN(parsed) ? undefined : parsed
  }

  return undefined
}

export const isDatabaseUnavailableError = (
  error: unknown
): error is DatabaseUnavailableError => {
  if (error instanceof DatabaseUnavailableError) return true

  return (
    isRecord(error) &&
    error.name === "DatabaseUnavailableError" &&
    typeof error.message === "string"
  )
}

export const isDirectusUnavailableError = (
  error: unknown,
  depth = 0
): boolean => {
  if (!error || depth > 4) return false

  if (isDatabaseUnavailableError(error)) return true

  if (typeof error === "string") {
    return DATABASE_UNAVAILABLE_MESSAGES.some((pattern) => pattern.test(error))
  }

  if (!isRecord(error)) return false

  const errorLike = error as ErrorLike
  const code = typeof errorLike.code === "string" ? errorLike.code : undefined
  const message =
    typeof errorLike.message === "string" ? errorLike.message : undefined
  const status =
    toStatus(errorLike.status) ??
    toStatus(errorLike.statusCode) ??
    (isRecord(errorLike.response)
      ? (toStatus(errorLike.response.status) ??
        toStatus(errorLike.response.statusCode))
      : undefined)

  if (code && DATABASE_UNAVAILABLE_CODES.has(code)) return true
  if (status && DATABASE_UNAVAILABLE_STATUSES.has(status)) return true
  if (
    message &&
    DATABASE_UNAVAILABLE_MESSAGES.some((pattern) => pattern.test(message))
  ) {
    return true
  }

  return [errorLike.cause, errorLike.response, errorLike.errors].some((value) =>
    Array.isArray(value)
      ? value.some((item) => isDirectusUnavailableError(item, depth + 1))
      : isDirectusUnavailableError(value, depth + 1)
  )
}

export const rethrowIfDatabaseUnavailable = (error: unknown) => {
  if (isDirectusUnavailableError(error)) {
    throw new DatabaseUnavailableError("Database unavailable", error)
  }
}
